import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';
import { ComponentProps } from '@algoan/deep-dive/model';

const messages = defineMessages({
  legal: {
    id: 'footer.legal_notice',
    defaultMessage: 'Legal notice',
    description: 'Title of the legal link',
  },
  privacy: {
    id: 'footer.privacy_policy',
    defaultMessage: 'Privacy policy',
    description: 'Title of the privacy link',
  },
  terms: {
    id: 'footer.terms',
    defaultMessage: 'Terms & Conditions',
    description: 'Title of the terms link',
  },
});

enum Language {
  FR = 'fr',
  EN = 'en-gb',
}

interface FooterProps extends ComponentProps {
  /**
   * Different style variant.
   * @default small
   */
  size?: 'small' | 'large';
  /** URL of the terms. Link is not visible if not provided */
  termsUrl?: string;
  /** Choose between white or blue background.
   * @default blue
   */
  variant?: 'blue' | 'white';
}

export const Footer = ({
  size = 'small',
  termsUrl,
  variant = 'blue',
  className,
  'data-testid': dataTestId,
}: FooterProps): JSX.Element => {
  const currentYear = new Date().getFullYear();

  const intl = useIntl();
  const language = intl.locale;

  const generateTermsAndConditionsUrl = (language: string): string => {
    return language === Language.FR
      ? 'https://www.algoan.com/fr/mentions-legales/'
      : 'https://www.algoan.com/legal-notice/';
  };

  const generatePrivacyPolicyUrl = (language: string): string => {
    return language === Language.FR
      ? 'https://www.algoan.com/fr/politique-de-confidentialite/'
      : 'https://www.algoan.com/data-privacy-policy/';
  };

  return (
    <FooterStyles.Container size={size} variant={variant} className={className} data-testid={dataTestId ?? 'footer'}>
      <FooterStyles.TextContainer size={size}>©Algoan {currentYear}</FooterStyles.TextContainer>
      <FooterStyles.TextContainer size={size}>
        <FooterStyles.StyledAnchor
          variant={variant}
          href={generateTermsAndConditionsUrl(language)}
          target="_blank"
          rel="noreferrer"
        >
          {intl.formatMessage(messages.legal)}
        </FooterStyles.StyledAnchor>
      </FooterStyles.TextContainer>
      <FooterStyles.TextContainer size={size}>
        <FooterStyles.StyledAnchor
          variant={variant}
          href={generatePrivacyPolicyUrl(language)}
          target="_blank"
          rel="noreferrer"
        >
          {intl.formatMessage(messages.privacy)}
        </FooterStyles.StyledAnchor>
      </FooterStyles.TextContainer>
      {termsUrl && (
        <FooterStyles.TextContainer size={size}>
          <FooterStyles.StyledAnchor variant={variant} href={termsUrl} target="_blank" rel="noreferrer">
            {intl.formatMessage(messages.terms)}
          </FooterStyles.StyledAnchor>
        </FooterStyles.TextContainer>
      )}
    </FooterStyles.Container>
  );
};

export const FooterStyles = {
  Container: styled.div<{ size: 'small' | 'large'; variant: 'blue' | 'white' }>`
    height: ${(props) => (props.size === 'large' ? '78px;' : '24px')};
    background-color: ${(props) => (props.variant === 'blue' ? props.theme.algoanBlue : props.theme.white)};
    width: 100%;
    display: flex;
    justify-content: center;
  `,
  TextContainer: styled.div<{ size: 'small' | 'large' }>`
    color: ${(props) => props.theme.lightBlue};
    text-align: center;
    line-height: ${(props) => (props.size === 'large' ? '78px;' : '24px')};
    font-size: ${(props) => (props.size === 'large' ? '16px;' : '12px')};
    user-select: none;
    margin-left: 16px;

    &:first-child {
      margin-left: 0;
    }
  `,
  StyledAnchor: styled.a<{ variant: 'blue' | 'white' }>`
    color: ${(props) => props.theme.lightBlue};
    text-decoration: none;
    transition-duration: 0.3s;

    &:hover {
      color: ${(props) => (props.variant === 'blue' ? props.theme.white : props.theme.strongGrey)};
      transition-duration: 0.3s;
    }
  `,
};
